<template>
  <PopoverMenu ref="popoverMenu" position="bottom-left" :has-arrow="false">
    <template #toggle-button>
      <ButtonItem
        class="views-toggle"
        theme="neutral"
        size="large"
        icon-position="right"
        right-icon="KeyboardArrowDown"
        :has-border="true"
        :text="toggleText"
        :data-qa="dataGridViewsDataQa.viewsMenuBtn"
      />
    </template>
    <template #popover-content>
      <div class="views-menu">
        <div class="views-section">
          <span class="body-regular-capital views-section-title">
            {{ t('dataGrid.views.sections.views') }}
          </span>
          <button
            v-for="view in views"
            :key="view.id"
            type="button"
            class="views-row"
            :data-qa="dataGridViewsDataQa.viewItem"
            @click="handleSelect(view.id)"
          >
            <SvgIcon
              value="Star"
              class="views-row-star"
              :class="{ 'views-row-star--default': view.isDefault }"
            />
            <span
              class="views-row-label"
              :class="{ 'views-row-label--active': isActiveView(view.id) }"
            >
              {{ viewDisplayName(view) }}
            </span>
            <SvgIcon v-if="isActiveView(view.id)" value="Check" class="views-row-check" />
          </button>
        </div>
        <div class="views-section">
          <span class="body-regular-capital views-section-title">
            {{ t('dataGrid.views.sections.settings') }}
          </span>
          <button
            type="button"
            class="views-row"
            :data-qa="dataGridViewsDataQa.manageViewsBtn"
            @click="handleManageViews"
          >
            <SvgIcon value="Configuration" class="views-row-icon" />
            <span class="views-row-label">{{ t('dataGrid.views.manageViews') }}</span>
          </button>
          <TooltipButton v-if="!hasDefaultView" :text="t('dataGrid.views.createNewViewDisabled')">
            <button
              type="button"
              class="views-row views-row--disabled"
              :data-qa="dataGridViewsDataQa.createNewViewBtn"
              disabled
            >
              <SvgIcon value="AddCircleOutline" class="views-row-icon" />
              <span class="views-row-label">{{ t('dataGrid.views.createNewView') }}</span>
            </button>
          </TooltipButton>
          <button
            v-else
            type="button"
            class="views-row"
            :data-qa="dataGridViewsDataQa.createNewViewBtn"
            @click="handleCreateNewView"
          >
            <SvgIcon value="AddCircleOutline" class="views-row-icon" />
            <span class="views-row-label">{{ t('dataGrid.views.createNewView') }}</span>
          </button>
        </div>
      </div>
    </template>
  </PopoverMenu>
</template>

<script setup lang="ts">
import { ButtonItem, PopoverMenu, SvgIcon } from '@lw-design-system/ui';
import { storeToRefs } from 'pinia';
import { useViewsStore } from '@/stores/views-store';
import { dataGridViewsDataQa } from '~/constants/commons/data-qa';
import TooltipButton from '~/components/common/item/TooltipButton.vue';
import type { SavedView } from '~/types/views';
import { getViewDisplayName } from '~/utils/view-display-name';

const { t } = useI18n();

const emit = defineEmits<{
  (e: 'select', viewId: string): void;
  (e: 'create-new-view'): void;
}>();

const viewsStore = useViewsStore();
const { views, activeViewId, defaultView } = storeToRefs(viewsStore);

const popoverMenu = ref<InstanceType<typeof PopoverMenu> | undefined>(undefined);

const viewDisplayName = (view: SavedView) => getViewDisplayName(view, t);

const toggleText = computed(() => {
  const activeView = viewsStore.activeView;
  return activeView ? viewDisplayName(activeView) : '';
});
const hasDefaultView = computed(() => Boolean(defaultView.value));

const isActiveView = (viewId: string) => viewId === activeViewId.value;

const handleSelect = (viewId: string) => {
  popoverMenu.value?.handleClick();
  emit('select', viewId);
};

const handleCreateNewView = () => {
  popoverMenu.value?.handleClick();
  emit('create-new-view');
};

const handleManageViews = () => {
  popoverMenu.value?.handleClick();
  viewsStore.openManageModal();
};
</script>

<style scoped>
.views-menu {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 14rem;
}

.views-section {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.views-section-title {
  padding: 0.5rem 1rem;
  color: var(--menu-item-text);
}

.views-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 0.5rem;
  text-align: left;
}

.views-row:hover {
  background: var(--menu-item-hover);
}

.views-row--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.views-row-star {
  width: 1.25rem;
  height: 1.25rem;
  color: var(--icon-light, #b6bcc9);
}

.views-row-star--default {
  color: var(--warning, #f5a623);
}

.views-row-label {
  flex: 1;
  color: var(--menu-item-text);
}

.views-row-label--active {
  font-weight: 500;
}

.views-row-check {
  width: 1.25rem;
  height: 1.25rem;
}

.views-row-icon {
  width: 1.25rem;
  height: 1.25rem;
  color: var(--menu-item-accent);
}
</style>
